"use client";

import NextImage from "next/image";

/**
 * BrochureBarBlock — horizontale CTA-strook "Vraag de gratis brochure aan".
 *
 * Port van Claude Design "Brochure Bar.html". 6 layout-varianten:
 *   - strip         : klassieke warm-cream strook + icon + tekst + knop
 *   - dark-contrast : dark band met brochure-visual + grote statement
 *   - inline-form   : e-mail input + direct aanvragen knop
 *   - split-panel   : ink-links (tekst + bullets), cream-rechts met brochure
 *   - pull-tab      : postkaart-kaart met perforatie + pull-tab knop
 *   - open-brochure : editorial flat-lay met open brochure-mockup
 */

import { useState } from "react";

export type BrochureBarVariant =
  | "strip"
  | "dark-contrast"
  | "inline-form"
  | "split-panel"
  | "pull-tab"
  | "open-brochure";

export interface BrochureBarContent {
  eyebrow?: string;
  headline?: string;
  body?: string;
  cta_text?: string;
  cta_href?: string;
  bullets?: string[];
  brochure_image_src?: string;
  email_placeholder?: string;
  email_submit_text?: string;
}

interface Props {
  content: BrochureBarContent;
  variant?: BrochureBarVariant;
}

const T = {
  bg: "var(--cream, #F1EEE7)",
  bgWarm: "var(--cream-dark, #EDE3D0)",
  bgDark: "#1A1F26",
  card: "#FFFFFF",
  ink: "var(--ink, #141C28)",
  muted: "var(--stone, #5A6578)",
  border: "rgba(20,28,40,0.10)",
  cta: "var(--terracotta, #FF6B1A)",
  gold: "#B8925A",
};

function unwrapStringList(raw: unknown, fallback: string[]): string[] {
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const out = raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "value" in item) {
        const v = (item as { value?: unknown }).value;
        return typeof v === "string" ? v : "";
      }
      return "";
    })
    .filter((s) => s.trim().length > 0);
  return out.length > 0 ? out : fallback;
}

function Arrow({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function EnvelopeIcon({ size = 20, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function BrochureMock({ w = 220, tilt = -4, src }: { w?: number; tilt?: number; src?: string }) {
  const h = Math.round(w * 0.6);
  if (src) {
    return (
      <NextImage
        src={src}
        alt="Gratis brochure badkamerrenovatie — Badkamer Wijzer"
        width={w}
        height={h}
        loading="lazy"
        sizes={`${w}px`}
        quality={85}
        style={{
          width: w,
          height: h,
          objectFit: "contain",
          transform: `rotate(${tilt}deg)`,
          filter: "drop-shadow(0 22px 32px rgba(20,28,40,0.28))",
          flexShrink: 0,
        }}
      />
    );
  }
  // Placeholder: dichte boekomslag met titel
  return (
    <div
      style={{
        width: w,
        height: h,
        background: `linear-gradient(145deg, ${T.bgWarm} 0%, #DFD3BA 100%)`,
        borderRadius: 4,
        boxShadow: "0 22px 32px -14px rgba(20,28,40,0.35)",
        transform: `rotate(${tilt}deg)`,
        padding: w * 0.05,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: T.ink,
        flexShrink: 0,
      }}
    >
      <div style={{ fontSize: w * 0.04, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.6 }}>Brochure</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: w * 0.08, fontWeight: 500, lineHeight: 1.05 }}>
        Uw nieuwe<br />
        <span style={{ fontStyle: "italic" }}>badkamer</span>
      </div>
    </div>
  );
}

// ── Variant 1: klassieke strip ────────────────────────────────────────────
function Strip({ content }: { content: BrochureBarContent }) {
  return (
    <section style={{ background: T.bgWarm, padding: "28px 28px", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 700px){ .brc-strip { flex-direction: column !important; text-align: center !important; gap: 16px !important; } }`}</style>
      <div className="brc-strip" style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 24 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: T.ink, color: T.bgWarm, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <EnvelopeIcon size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(20px, 2.2vw, 26px)", fontWeight: 500, color: T.ink, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
            {content.headline ?? (
              <>
                Ontvang onze <span style={{ fontStyle: "italic" }}>gratis</span> brochure
              </>
            )}
          </div>
          <div style={{ fontSize: 14.5, color: T.muted, marginTop: 4 }}>
            {content.body ?? "55 pagina's inspiratie, gratis thuisgestuurd · geen verplichting"}
          </div>
        </div>
        <a href={content.cta_href ?? "#brochure-form"} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "14px 24px", borderRadius: 999, background: T.cta, color: "white", fontSize: 15, fontWeight: 600, textDecoration: "none", boxShadow: "0 12px 24px -12px rgba(0,0,0,0.3)", flexShrink: 0 }}>
          {content.cta_text ?? "Vraag aan"} <Arrow />
        </a>
      </div>
    </section>
  );
}

// ── Variant 2: dark contrast ──────────────────────────────────────────────
function DarkContrast({ content }: { content: BrochureBarContent }) {
  return (
    <section style={{ background: T.bgDark, color: "#F1EEE7", padding: "42px 28px", fontFamily: "system-ui, sans-serif", position: "relative", overflow: "hidden" }}>
      <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.05, backgroundImage: "repeating-linear-gradient(115deg, #F1EEE7 0 1px, transparent 1px 40px)" }} />
      <style>{`@media (max-width: 1024px){ .brc-dark { flex-direction: column !important; text-align: center !important; } }`}</style>
      <div className="brc-dark" style={{ position: "relative", maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 44 }}>
        <BrochureMock w={320} tilt={-5} {...(content.brochure_image_src ? { src: content.brochure_image_src } : {})} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.cta, textTransform: "uppercase", marginBottom: 10 }}>
            {content.eyebrow ?? `Brochure · Editie ${new Date().getFullYear()}`}
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, color: "#F1EEE7", lineHeight: 1.1, letterSpacing: "-0.01em", margin: "0 0 8px" }}>
            {content.headline ?? (
              <>
                Uw volgende badkamer begint <span style={{ fontStyle: "italic" }}>op uw deurmat.</span>
              </>
            )}
          </h3>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: "rgba(241,238,231,0.72)", margin: "0 0 18px", maxWidth: 560 }}>
            {content.body ?? "Inspiratie, stijlen, materialen en concrete voorbeelden van prijzen. Gratis bij u thuis, zonder verplichting."}
          </p>
          <a href={content.cta_href ?? "#brochure-form"} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "14px 26px", borderRadius: 999, background: T.cta, color: "white", fontSize: 15, fontWeight: 600, textDecoration: "none", boxShadow: "0 12px 28px -10px rgba(0,0,0,0.5)" }}>
            <EnvelopeIcon size={17} /> {content.cta_text ?? "Vraag de brochure aan"} <Arrow />
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Variant 3: inline form ───────────────────────────────────────────────
function InlineForm({ content }: { content: BrochureBarContent }) {
  const [email, setEmail] = useState("");
  return (
    <section style={{ background: T.bg, padding: "40px 28px", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .brc-inline { grid-template-columns: 1fr !important; gap: 18px !important; } }`}</style>
      <div className="brc-inline" style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr auto", gap: 32, alignItems: "center" }}>
        <div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(22px, 2.5vw, 30px)", fontWeight: 500, color: T.ink, lineHeight: 1.15, letterSpacing: "-0.01em", margin: "0 0 6px" }}>
            {content.headline ?? "De brochure in uw brievenbus."}
          </h3>
          <p style={{ fontSize: 14.5, color: T.muted, margin: 0, lineHeight: 1.5 }}>
            {content.body ?? "Vul uw e-mailadres in — wij sturen hem gratis na."}
          </p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (email && content.cta_href) window.location.href = `${content.cta_href}?email=${encodeURIComponent(email)}`;
          }}
          style={{ display: "flex", gap: 8, background: T.card, padding: 6, borderRadius: 999, border: `1px solid ${T.border}`, boxShadow: "0 4px 12px -8px rgba(0,0,0,0.12)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px 0 18px", flex: 1 }}>
            <EnvelopeIcon size={16} color={T.muted} />
            <input
              type="email"
              placeholder={content.email_placeholder ?? "uw@email.nl"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ border: "none", outline: "none", background: "transparent", fontSize: 15, color: T.ink, width: "100%", fontFamily: "inherit", padding: "14px 0" }}
            />
          </div>
          <button type="submit" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 24px", borderRadius: 999, background: T.cta, color: "white", fontSize: 14.5, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
            {content.email_submit_text ?? content.cta_text ?? "Stuur brochure"} <Arrow />
          </button>
        </form>
      </div>
    </section>
  );
}

// ── Variant 4: split panel ───────────────────────────────────────────────
function SplitPanel({ content }: { content: BrochureBarContent }) {
  const bullets = unwrapStringList(content.bullets, ["Inspiratie per stijl", "Prijsvoorbeelden", "Ons werkproces, stap voor stap"]);
  return (
    <section style={{ background: T.bg, padding: "40px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .brc-split { grid-template-columns: 1fr !important; } }`}</style>
      <div className="brc-split" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1.2fr 1fr", borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 40px -22px rgba(0,0,0,0.18)" }}>
        <div style={{ background: T.bgDark, color: "#F1EEE7", padding: "44px 44px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.cta, textTransform: "uppercase", marginBottom: 10 }}>
            {content.eyebrow ?? "Gratis brochure"}
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(22px, 2.8vw, 34px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.01em", margin: "0 0 14px" }}>
            {content.headline ?? (
              <>
                55 pagina's vol <span style={{ fontStyle: "italic" }}>mogelijkheden.</span>
              </>
            )}
          </h3>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", fontSize: 14.5, lineHeight: 1.9, color: "rgba(241,238,231,0.78)" }}>
            {bullets.map((t, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.cta, flexShrink: 0 }} />
                {t}
              </li>
            ))}
          </ul>
          <a href={content.cta_href ?? "#brochure-form"} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 10, padding: "13px 24px", borderRadius: 999, background: T.cta, color: "white", fontSize: 14.5, fontWeight: 600, textDecoration: "none" }}>
            {content.cta_text ?? "Vraag gratis aan"} <Arrow />
          </a>
        </div>
        <div style={{ background: `linear-gradient(145deg, ${T.bgWarm} 0%, #DFD3BA 100%)`, display: "grid", placeItems: "center", padding: "40px 24px", minHeight: 260 }}>
          <BrochureMock w={300} tilt={-5} {...(content.brochure_image_src ? { src: content.brochure_image_src } : {})} />
        </div>
      </div>
    </section>
  );
}

// ── Variant 5: pull-tab postkaart ────────────────────────────────────────
function PullTab({ content }: { content: BrochureBarContent }) {
  return (
    <section style={{ background: T.bg, padding: "48px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .brc-pull { grid-template-columns: 1fr !important; } .brc-pull > a { border-left: none !important; border-top: 3px dashed rgba(255,255,255,0.55) !important; } }`}</style>
      <div className="brc-pull" style={{ maxWidth: 1000, margin: "0 auto", background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: "0 22px 44px -28px rgba(0,0,0,0.24)", overflow: "hidden", display: "grid", gridTemplateColumns: "1fr auto" }}>
        <div style={{ padding: "32px 40px", display: "flex", alignItems: "center", gap: 22 }}>
          <div style={{ width: 90, height: 90, flexShrink: 0, borderRadius: "50%", border: `2px dashed ${T.gold}`, display: "grid", placeItems: "center", fontFamily: "'Fraunces', serif", fontStyle: "italic", color: T.gold, transform: "rotate(-8deg)", lineHeight: 1.05, textAlign: "center", fontSize: 13, fontWeight: 500, padding: 6 }}>
            gratis
            <br />
            <span style={{ fontSize: 24, fontWeight: 600, fontStyle: "normal", display: "block", marginTop: 2, color: T.ink }}>brochure</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.muted, textTransform: "uppercase", marginBottom: 6 }}>
              {content.eyebrow ?? "Thuisgestuurd · Geen verplichting"}
            </div>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(20px, 2.4vw, 28px)", fontWeight: 500, color: T.ink, lineHeight: 1.15, letterSpacing: "-0.01em", margin: "0 0 4px" }}>
              {content.headline ?? (
                <>
                  Een boekje vol ideeën — <span style={{ fontStyle: "italic" }}>aan u.</span>
                </>
              )}
            </h3>
            <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
              {content.body ?? "Vul uw adres in en hij ligt binnen 2 dagen op uw deurmat."}
            </div>
          </div>
        </div>
        <a
          href={content.cta_href ?? "#brochure-form"}
          style={{
            background: T.cta,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px 36px",
            fontFamily: "'Fraunces', serif",
            fontSize: 20,
            fontWeight: 500,
            textDecoration: "none",
            letterSpacing: "-0.005em",
            minWidth: 180,
            borderLeft: "3px dashed rgba(255,255,255,0.55)",
            gap: 10,
          }}
        >
          {content.cta_text ?? "Aanvragen"} <Arrow size={18} />
        </a>
      </div>
    </section>
  );
}

// ── Variant 6: open brochure editorial ───────────────────────────────────
function OpenBrochure({ content }: { content: BrochureBarContent }) {
  return (
    <section style={{ background: `linear-gradient(135deg, ${T.bgWarm} 0%, #E4D6BC 100%)`, padding: "50px 28px", fontFamily: "system-ui, sans-serif", position: "relative", overflow: "hidden", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
      <style>{`@media (max-width: 1024px){ .brc-open { grid-template-columns: 1fr !important; text-align: center !important; } .brc-open > :last-child { display: flex; justify-content: center; } }`}</style>
      <div className="brc-open" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 40 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.cta, textTransform: "uppercase", marginBottom: 12 }}>
            {content.eyebrow ?? `Editie ${new Date().getFullYear()} · 55 pagina's`}
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(26px, 3.4vw, 42px)", fontWeight: 500, color: T.ink, lineHeight: 1.05, letterSpacing: "-0.015em", margin: "0 0 12px", textWrap: "balance" }}>
            {content.headline ?? (
              <>
                Blader door uw
                <br />
                <span style={{ fontStyle: "italic" }}>nieuwe</span> badkamer.
              </>
            )}
          </h3>
          <p style={{ fontSize: 15.5, color: T.muted, lineHeight: 1.6, margin: "0 0 22px", maxWidth: 460 }}>
            {content.body ?? "Inspiratie, stijlen en prijsvoorbeelden. Gratis thuisgestuurd in een mooie editie — zonder verplichtingen."}
          </p>
          <a href={content.cta_href ?? "#brochure-form"} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "14px 26px", borderRadius: 999, background: T.cta, color: "white", fontSize: 15, fontWeight: 600, textDecoration: "none", boxShadow: "0 12px 28px -10px rgba(0,0,0,0.3)" }}>
            {content.cta_text ?? "Vraag gratis aan"} <Arrow />
          </a>
        </div>
        <div>
          <BrochureMock w={340} tilt={-4} {...(content.brochure_image_src ? { src: content.brochure_image_src } : {})} />
        </div>
      </div>
    </section>
  );
}

export default function BrochureBarBlock({ content, variant = "strip" }: Props) {
  switch (variant) {
    case "dark-contrast":
      return <DarkContrast content={content} />;
    case "inline-form":
      return <InlineForm content={content} />;
    case "split-panel":
      return <SplitPanel content={content} />;
    case "pull-tab":
      return <PullTab content={content} />;
    case "open-brochure":
      return <OpenBrochure content={content} />;
    case "strip":
    default:
      return <Strip content={content} />;
  }
}
